import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import InstallAppButton from "@/components/InstallAppButton";
import { SetuMark } from "@/components/Logo";
import {
  ArrowUpRight, ShieldCheck, ScanEye, MessageSquareText, Mic,
  QrCode, CloudSun, CheckCircle2, FileText, Fingerprint, Sparkles,
} from "lucide-react";

const Feature = ({ icon: Icon, title, desc, testid, slug }) => (
  <Link to={`/features#${slug}`} data-testid={testid} className="card-lift border border-border bg-card p-6 sm:p-8 group block">
    <div className="flex items-start justify-between mb-4 sm:mb-6">
      <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center">
        <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
      </div>
    </div>
    <div className="font-heading text-lg sm:text-xl mb-2">{title}</div>
    <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
    <div className="mt-4 sm:mt-6 inline-flex items-center gap-1 text-sm text-primary sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
      Learn more <ArrowUpRight className="h-3.5 w-3.5" />
    </div>
  </Link>
);

const PricingTier = ({ name, price, features, highlighted, testid, badge, t }) => (
  <div data-testid={testid}
    className={`card-lift relative border p-6 sm:p-8 flex flex-col rounded-xl ${
      highlighted ? "border-primary bg-card ring-1 ring-primary/40" : "border-border bg-card"
    }`}>
    {badge && (
      <div className="absolute -top-3 left-6 sm:left-8 mono text-[9px] uppercase tracking-[0.15em] bg-primary text-primary-foreground px-2.5 py-1 rounded">
        {badge}
      </div>
    )}
    <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("plan")}</div>
    <div className="font-display text-4xl sm:text-5xl mt-2">{name}</div>
    <div className="mt-4 flex items-baseline gap-1">
      <span className="font-heading text-4xl">₹{price}</span>
      <span className="text-muted-foreground text-sm">/{t("monthly").toLowerCase()}</span>
    </div>
    <div className="rule-ornament my-6"></div>
    <ul className="space-y-3 text-sm flex-1">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-2.5">
          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={2} />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    <Link to="/signup"
      data-testid={`${testid}-cta`}
      className={`btn-sharp mt-8 inline-flex items-center justify-center gap-2 px-4 py-3 font-medium ${
        highlighted ? "bg-primary text-primary-foreground" : "border border-border hover:bg-muted"
      }`}>
      {t("start_with")} {name} <ArrowUpRight className="h-4 w-4" />
    </Link>
  </div>
);

export default function Landing() {
  const { t } = useLang();
  return (
    <div className="min-h-screen grain-bg">
      {/* Header */}
      <header className="border-b border-border/60 bg-background/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0" data-testid="logo-link">
            <div className="w-8 h-8 rounded-md bg-primary grid place-items-center text-primary-foreground shrink-0"><SetuMark size={22} strokeWidth={2.6} /></div>
            <div className="flex items-center gap-2">
              <span className="font-heading text-base sm:text-lg tracking-tight">Setubuild.OS</span>
              <span data-testid="the-bridge-badge" className="hidden md:inline-flex mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground border border-border rounded px-1.5 py-0.5">The bridge</span>
            </div>
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <InstallAppButton />
            <LanguageSwitcher />
            <ThemeToggle />
            <Link data-testid="nav-signin" to="/login" className="btn-sharp hidden sm:inline-flex px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
              {t("sign_in")}
            </Link>
            <Link data-testid="nav-getstarted" to="/signup" className="btn-sharp inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-foreground text-background hover:opacity-90 whitespace-nowrap">
              {t("get_started")} <ArrowUpRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 blueprint-grid opacity-40" />
        <div
          className="absolute inset-0 opacity-[0.07] dark:opacity-[0.12] pointer-events-none bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=2400&q=70')",
            mixBlendMode: "luminosity",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-16 sm:py-24 md:py-40 relative">
          <div className="fade-in-up">
            <div className="inline-flex items-center gap-2 mono text-[10px] sm:text-xs text-muted-foreground border border-border rounded-full px-3 py-1.5 mb-6 sm:mb-8">
              <Sparkles className="h-3 w-3 text-primary" />
              <span className="uppercase tracking-[0.15em]">{t("hero_pill")}</span>
            </div>

            <h1 className="font-display text-4xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[1.05] sm:leading-[0.95] max-w-5xl">
              {t("hero_title_a")} <span className="font-display-italic text-primary">{t("hero_title_b")}</span><br />
              {t("hero_title_c")}
            </h1>

            <p className="mt-6 sm:mt-10 text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              {t("hero_desc")}
            </p>

            <div className="mt-8 sm:mt-12 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="hero-cta-primary"
                className="btn-sharp inline-flex items-center gap-2 bg-foreground text-background px-5 sm:px-6 py-3 sm:py-3.5 text-sm sm:text-base font-medium">
                {t("start_free_14")} <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/login" data-testid="hero-cta-secondary"
                className="btn-sharp inline-flex items-center gap-2 border border-border px-5 sm:px-6 py-3 sm:py-3.5 text-sm sm:text-base font-medium hover:bg-muted">
                {t("sign_in")}
              </Link>
            </div>
          </div>

          <div className="mt-16 sm:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 max-w-4xl">
            {[
              ["4", t("stat_ai_models")],
              ["17", t("stat_languages")],
              ["0", t("stat_servers")],
              ["99.4%", t("stat_uptime")],
            ].map(([n, l]) => (
              <div key={l} className="border-l border-border pl-3 sm:pl-4">
                <div className="font-display text-3xl sm:text-5xl text-foreground">{n}</div>
                <div className="mono text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial pull quote */}
      <section className="relative border-b border-border overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08] dark:opacity-[0.15] pointer-events-none bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=2400&q=70')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-16 sm:py-24 md:py-32 relative">
                    <p className="font-display text-2xl sm:text-3xl md:text-5xl leading-tight">
            {t("quote_a")} <span className="font-display-italic text-primary">{t("quote_b")}</span> {t("quote_c")}
          </p>
          <div className="mt-6 sm:mt-8 mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {t("quote_source")}
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-16 sm:py-24 md:py-32">
        <div className="max-w-2xl mb-10 sm:mb-16">
                    <h2 className="font-display text-4xl sm:text-5xl md:text-6xl leading-tight">
            {t("features_h_a")} <span className="font-display-italic text-primary">{t("features_h_b")}</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <Feature icon={ScanEye} title={t("f_inspector")} slug="photo-inspector" testid="feature-inspector"
            desc={t("f_inspector_d")} />
          <Feature icon={MessageSquareText} title={t("f_chat")} slug="civil-ai" testid="feature-chat"
            desc={t("f_chat_d")} />
          <Feature icon={FileText} title={t("f_reports")} slug="daily-reports" testid="feature-reports"
            desc={t("f_reports_d")} />
          <Feature icon={Fingerprint} title={t("f_attendance")} slug="attendance" testid="feature-attendance"
            desc={t("f_attendance_d")} />
          <Feature icon={Mic} title={t("f_voice")} slug="voice-logs" testid="feature-voice"
            desc={t("f_voice_d")} />
          <Feature icon={ShieldCheck} title={t("f_safety")} slug="safety" testid="feature-safety"
            desc={t("f_safety_d")} />
          <Feature icon={QrCode} title={t("f_equipment")} slug="equipment" testid="feature-equipment"
            desc={t("f_equipment_d")} />
          <Feature icon={CloudSun} title={t("f_weather")} slug="weather" testid="feature-weather"
            desc={t("f_weather_d")} />
        </div>
      </section>

      {/* Bold visual break */}
      <section className="relative border-y border-border overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=2400&q=75')",
          }}
        />
        <div className="absolute inset-0 bg-background/70 dark:bg-background/80 backdrop-blur-[2px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-16 sm:py-24 md:py-32 text-center">
                    <h2 className="font-display text-4xl sm:text-5xl md:text-7xl leading-[1.05] sm:leading-[0.95] max-w-4xl mx-auto">
            {t("break_h_a")} <span className="font-display-italic text-primary">{t("break_h_b")}</span>
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-muted-foreground text-sm sm:text-base px-2">
            {t("break_p")}
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-muted/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-16 sm:py-24 md:py-32">
          <div className="max-w-2xl mb-10 sm:mb-16">
                        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl leading-tight">
              {t("pricing_h_a")} <span className="font-display-italic text-primary">{t("pricing_h_b")}</span>
            </h2>
            <p className="mt-4 sm:mt-6 text-muted-foreground text-sm sm:text-base">
              {t("pricing_desc")}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
            <PricingTier name={t("starter")} price="999" testid="plan-starter" t={t}
              features={[t("p_starter_1"), t("p_starter_2"), t("p_starter_3"), t("p_starter_4")]} />
            <PricingTier name={t("pro")} price="2,499" highlighted badge={t("badge_popular")} testid="plan-pro" t={t}
              features={[t("p_pro_1"), t("p_pro_2"), t("p_pro_3"), t("p_pro_4"), t("p_pro_5")]} />
            <PricingTier name={t("enterprise")} price="7,999" testid="plan-enterprise" t={t}
              features={[t("p_ent_1"), t("p_ent_2"), t("p_ent_3"), t("p_ent_4"), t("p_ent_5")]} />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-16 sm:py-24 md:py-32 text-center">
                    <h2 className="font-display text-4xl sm:text-5xl md:text-7xl leading-tight">
            {t("cta_h_a")} <span className="font-display-italic text-primary">{t("cta_h_b")}</span>
          </h2>
          <p className="mt-4 sm:mt-6 text-muted-foreground max-w-xl mx-auto text-sm sm:text-base px-2">
            {t("cta_desc")}
          </p>
          <div className="mt-8 sm:mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/signup" data-testid="cta-signup"
              className="btn-sharp inline-flex items-center gap-2 bg-foreground text-background px-6 sm:px-8 py-3 sm:py-3.5 text-sm sm:text-base font-medium">
              {t("start_free_14")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-8 sm:py-10 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded bg-primary grid place-items-center text-primary-foreground"><SetuMark size={16} strokeWidth={2.8} /></div>
            <div className="mono text-[10px] sm:text-xs text-muted-foreground">© {new Date().getFullYear()} SetuBuild Technologies. All rights reserved.</div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 sm:gap-6 mono text-[10px] uppercase tracking-[0.2em]">
            <Link to="/about" className="text-muted-foreground hover:text-foreground" data-testid="footer-about">{t("about")}</Link>
            <Link to="/contact" className="text-muted-foreground hover:text-foreground" data-testid="footer-contact">{t("contact")}</Link>
            <Link to="/faq" className="text-muted-foreground hover:text-foreground" data-testid="footer-faq">{t("faq")}</Link>
            <Link to="/privacy" className="text-muted-foreground hover:text-foreground" data-testid="footer-privacy">{t("privacy")}</Link>
            <Link to="/terms" className="text-muted-foreground hover:text-foreground" data-testid="footer-terms">{t("terms")}</Link>
            <Link to="/refund" className="text-muted-foreground hover:text-foreground" data-testid="footer-refund">{t("refund")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
